import { ResultOf } from "@graphql-typed-document-node/core";
import { useMemo } from "react";
import { CombinedError, useQuery } from "urql";

import { getFragmentData, graphql } from "@/gen";
import { GameReportQueryQuery } from "@/gen/graphql";
import { LanguageCode } from "@/utils/i18n";

const useGame_Game = graphql(`
    fragment useGame_Game on Game {
        id
        result
        startedAt
        scheduledStartAt
        startingPositionFen
        player1Color
        player2Color
        trial {
            __typename
            id
        }
        whitePlayer {
            id
            name
            nickname
            fideId
            imageId
        }
        blackPlayer {
            id
            name
            nickname
            fideId
            imageId
        }
        finishedAt
        heroImageId
        pgnRoundTag
    }
`);

export type GameT = ResultOf<typeof useGame_Game>;

export const useGame: (gameId?: string) => {
    game: GameT | null | undefined;
    loading: boolean;
    error: CombinedError | undefined;
    refetch: () => void;
} = (gameId?: string) => {
    const [result, refetch] = useQuery({
        query: graphql(`
            query gameQuery($id: UUID!) {
                game(id: $id) {
                    ...useGame_Game
                }
            }
        `),
        variables: {
            id: gameId!,
        },
        pause: !gameId,
    });

    const game = useMemo(() => getFragmentData(useGame_Game, result.data?.game), [result.data]);

    return {
        game,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
};

export function useGameReport(
    gameId: string,
    languageCode: LanguageCode
): {
    game: GameReportQueryQuery["game"] | null | undefined;
    loading: boolean;
    error: CombinedError | undefined;
    refetch: () => void;
} {
    const [result, refetch] = useQuery({
        query: graphql(`
            query gameReportQuery($id: UUID!, $gameReportInput: GameReportInput!) {
                game(id: $id) {
                    id
                    whitePlayer {
                        nickname
                    }
                    blackPlayer {
                        nickname
                    }
                    report(input: $gameReportInput) {
                        id
                        ...GameReportForm_GameReport
                    }
                }
            }
        `),
        variables: {
            id: gameId,
            gameReportInput: {
                languageCode,
            },
        },
    });

    return {
        game: result.data?.game,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
}
